import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  makeEnvironmentProviders,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withViewTransitions,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import * as fromSeriousSystem from '@serious-system';
import { MarkdownModule } from 'ngx-markdown';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import * as fromGenerated from './_generated';
import { provideTranslation, provideTranslationInitializer } from './app.i18n';
import { appRoutes } from './app.routes';
import * as fromAuth from './auth';
import * as fromFavorites from './favorites';
import * as fromFeatureFlags from './feature-flags';
import { languageInterceptor } from './language.interceptor';
import * as fromRefiner from './refiner';
import { appFeature } from './store/app.reducer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideToastr({
      timeOut: 4000,
      tapToDismiss: false,
      preventDuplicates: false,
      enableHtml: false,
      iconClasses: {
        info: 'sd-toast-info',
        success: 'sd-toast-success',
        warning: 'sd-toast-warning',
        error: 'sd-toast-error',
      },
      positionClass: 'toast-top-right',
      messageClass: 'sd-toast-message',
      toastClass: 'sd-toast',
      maxOpened: 0, // No max
      easeTime: 0,
      toastComponent: fromSeriousSystem.ToastrComponent,
    }),
    provideEffects([
      fromAuth.AuthEffects,
      fromFeatureFlags.FeatureFlagsEffects,
      fromFavorites.AssistantFavoritesEffects,
    ]),
    provideStore(),
    provideState(appFeature),
    provideState(fromAuth.authFeature),
    provideState(fromFavorites.assistantFavoritesFeature),
    provideState(fromFeatureFlags.featureFlagsFeature),
    provideStoreDevtools({ maxAge: 50, logOnly: !isDevMode() }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withViewTransitions(),
      withComponentInputBinding(),
    ),
    provideHttpClient(
      withInterceptors([fromAuth.authInterceptor, languageInterceptor]),
    ),
    importProvidersFrom([TranslateModule.forRoot(provideTranslation())]),
    provideTranslationInitializer(),
    importProvidersFrom([MarkdownModule.forRoot()]),
    importProvidersFrom([
      fromGenerated.ApiModule.forRoot(
        () =>
          new fromGenerated.Configuration({
            basePath: environment.appApiUrl,
            withCredentials: true,
          }),
      ),
    ]),
    makeEnvironmentProviders([
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      fromFeatureFlags.provideLaunchDarklyInitializer(),
      fromRefiner.provideRefinerInitializer(),
    ]),
  ],
};
